import React, { useMemo } from "react";
import {
  Dialog,
  Typography,
  IconButton,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { getSignColor } from "../../PerformanceDashboard/utility";
import {
  v2CardBg,
  v2CardBorder,
  v2LabelSx,
} from "../../PerformanceDashboardV2/v2Theme";
import { getSideHeaderSx, cellSx } from "./positionTableV2Styles";
import {
  buildTopMoversFilterLabel,
  formatMoverPl,
  getTopMovers,
} from "../utils/topMoversUtils";

const MOVER_COLUMNS = [
  { id: "ticker", label: "Ticker", align: "left" },
  { id: "side", label: "Side", align: "left" },
  { id: "pl", label: "Today P/L", align: "right" },
];

const MoversTable = ({ title, side, rows, theme }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      borderRadius: 2,
      overflow: "hidden",
      border: "1px solid",
      borderColor: v2CardBorder(theme),
      backgroundColor: v2CardBg(theme),
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={getSideHeaderSx(theme, side)}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, fontSize: "0.95rem", ...v2LabelSx(theme) }}
      >
        {title}
      </Typography>
    </Box>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {MOVER_COLUMNS.map((col) => (
              <TableCell
                key={col.id}
                align={col.align}
                sx={{
                  ...cellSx,
                  borderColor: v2CardBorder(theme),
                  backgroundColor: v2CardBg(theme),
                  color: "text.secondary",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${side}-${row.ticker}-${row.pl}`} hover>
              <TableCell
                align="left"
                sx={{ ...cellSx, borderColor: v2CardBorder(theme) }}
              >
                {row.ticker}
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...cellSx, borderColor: v2CardBorder(theme) }}
              >
                {row.side}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...cellSx,
                  borderColor: v2CardBorder(theme),
                  color: getSignColor(row.pl, theme),
                }}
              >
                {formatMoverPl(row.pl)}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell
                colSpan={MOVER_COLUMNS.length}
                align="center"
                sx={{ border: "none", py: 4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  No positions
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const TopMoversDialogV2 = ({
  open,
  onClose,
  data = [],
  fundVal,
  strategyVal,
  side = "Both",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const filterLabel = useMemo(
    () => buildTopMoversFilterLabel({ fundVal, strategyVal, side }),
    [fundVal, strategyVal, side]
  );

  const { gainers, losers, totalEligible } = useMemo(
    () => getTopMovers(data, { count: 5 }),
    [data]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            border: "1px solid",
            borderColor: v2CardBorder(theme),
            backgroundColor: isDark ? theme.palette.grey[50] : "#ebebeb",
            backgroundImage: "none",
          },
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.25, pb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.15rem", letterSpacing: "-0.01em" }}
            >
              Top movers
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontSize: "0.9rem", ...v2LabelSx(theme) }}
            >
              {filterLabel} · Today&apos;s P/L
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, ...v2LabelSx(theme) }}
            >
              Based on {totalEligible} positions currently in the table
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close top movers"
            size="small"
            sx={{
              color: "text.secondary",
              border: "1px solid",
              borderColor: v2CardBorder(theme),
              borderRadius: 1.5,
              flexShrink: 0,
              "&:hover": {
                color: "text.primary",
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <MoversTable
            title="Top gainers"
            side="long"
            rows={gainers}
            theme={theme}
          />
          <MoversTable
            title="Top losers"
            side="short"
            rows={losers}
            theme={theme}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default TopMoversDialogV2;
